import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ExpensesMonthly } from '../../types';
import { formatMoney } from '../commercial/contact';

// Misma paleta que el Tablero comercial.
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const OTHER = '#8a8a86';
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const monthLabel = (k: string) => { const [y, m] = k.split('-'); return `${MONTHS[Number(m) - 1]} ${y.slice(2)}`; };
const axisTick = { fontSize: 12, fill: '#52514e' };
const kTick = (v: number) => `$${Math.round(v / 1000).toLocaleString('es-AR')}k`;
const money = (v: any) => formatMoney(Number(v));

/** Columnas de facturación contra costo de mercadería + gastos, con la línea de beneficio. */
export const RevenueVsExpensesChart: React.FC<{ data: ExpensesMonthly; height?: number }> = ({ data, height = 280 }) => {
  const chart = data.series.map(s => ({
    month: monthLabel(s.month),
    Facturación: s.revenue,
    'Costo mercadería': s.cogs,
    Gastos: s.expenses,
    Beneficio: s.profit,
  }));
  return (
    <Box sx={{ height }}>
      <ResponsiveContainer>
        <ComposedChart data={chart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="25%">
          <CartesianGrid vertical={false} stroke="#e7e6e2" />
          <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
          <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={kTick} width={64} />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} formatter={money} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine y={0} stroke="#a3a29e" />
          <Bar dataKey="Facturación" fill={SERIES[0]} radius={[4, 4, 0, 0]} />
          <Bar dataKey="Costo mercadería" stackId="out" fill={OTHER} />
          <Bar dataKey="Gastos" stackId="out" fill={SERIES[1]} radius={[4, 4, 0, 0]} />
          <Line type="linear" dataKey="Beneficio" stroke={SERIES[2]} strokeWidth={2.5} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

/** Columnas de gastos por mes apiladas por categoría (las 3-4 más grandes con color, el resto en "Otras"). */
export const ExpensesByCategoryChart: React.FC<{ data: ExpensesMonthly; height?: number }> = ({ data, height = 280 }) => {
  const { series, chart } = useMemo(() => {
    const totals = data.categories
      .map(c => ({ c, total: data.series.reduce((acc, s) => acc + (s.byCategory[c] || 0), 0) }))
      .filter(x => x.total > 0)
      .sort((a, b) => b.total - a.total);
    const top = totals.slice(0, totals.length > 4 ? 3 : 4).map(x => x.c);
    const rest = totals.slice(top.length).map(x => x.c);
    const ser = top.map((name, i) => ({ name, color: SERIES[i] }));
    if (rest.length) ser.push({ name: 'Otras', color: OTHER });
    const rows = data.series.map(s => {
      const row: any = { month: monthLabel(s.month) };
      top.forEach(c => { row[c] = s.byCategory[c] || 0; });
      if (rest.length) row.Otras = rest.reduce((acc, c) => acc + (s.byCategory[c] || 0), 0);
      return row;
    });
    return { series: ser, chart: rows };
  }, [data]);

  if (series.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Todavía no hay gastos cargados.</Typography>;
  }
  return (
    <Box sx={{ height }}>
      <ResponsiveContainer>
        <BarChart data={chart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid vertical={false} stroke="#e7e6e2" />
          <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
          <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={kTick} width={64} />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} formatter={money} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {series.map((s, i) => (
            <Bar key={s.name} dataKey={s.name} stackId="cat" fill={s.color}
              radius={i === series.length - 1 ? [4, 4, 0, 0] : undefined} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const budgetChip = (actual: number, budget: number) => {
  if (budget <= 0) return null;
  if (actual > budget) return <Chip size="small" color="error" label="Excedido" />;
  if (actual > budget * 0.9) return <Chip size="small" color="warning" label="Al límite" />;
  return <Chip size="small" color="success" label="OK" />;
};

/** Tabla mes a mes: facturación, costo de mercadería, gastos, presupuesto, beneficio y margen. */
export const MonthlyResultsTable: React.FC<{ data: ExpensesMonthly }> = ({ data }) => {
  const rows = [...data.series].reverse();
  const hide = { display: { xs: 'none', md: 'table-cell' } };
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Mes</TableCell>
            <TableCell align="right">Facturación</TableCell>
            <TableCell align="right" sx={hide}>Costo mercadería</TableCell>
            <TableCell align="right" sx={hide}>Fijos</TableCell>
            <TableCell align="right" sx={hide}>Día a día</TableCell>
            <TableCell align="right">Gastos</TableCell>
            <TableCell align="right" sx={hide}>Presupuesto</TableCell>
            <TableCell align="right">Beneficio</TableCell>
            <TableCell align="right">Margen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(s => (
            <TableRow key={s.month}>
              <TableCell>{monthLabel(s.month)}</TableCell>
              <TableCell align="right">{formatMoney(s.revenue)}</TableCell>
              <TableCell align="right" sx={hide}>{formatMoney(s.cogs)}</TableCell>
              <TableCell align="right" sx={hide}>{formatMoney(s.fixed)}</TableCell>
              <TableCell align="right" sx={hide}>{formatMoney(s.variable)}</TableCell>
              <TableCell align="right"><b>{formatMoney(s.expenses)}</b></TableCell>
              <TableCell align="right" sx={hide}>
                {s.budget > 0 ? <>{formatMoney(s.budget)} {budgetChip(s.expenses, s.budget)}</> : '—'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: s.profit < 0 ? 'error.main' : 'success.main' }}>{formatMoney(s.profit)}</TableCell>
              <TableCell align="right">{s.marginPct == null ? '—' : `${s.marginPct}%`}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** Vista completa para la pestaña "Mes a mes" de Gastos. */
const ExpensesMonthlyView: React.FC<{ data: ExpensesMonthly }> = ({ data }) => {
  const t = data.totals;
  const margin = t.revenue > 0 ? Math.round((t.profit / t.revenue) * 1000) / 10 : null;
  const tiles = [
    { label: 'Facturación', value: formatMoney(t.revenue) },
    { label: 'Costo de la mercadería', value: formatMoney(t.cogs) },
    { label: 'Gastos', value: formatMoney(t.expenses) },
    { label: 'Beneficio', value: formatMoney(t.profit), color: t.profit < 0 ? 'error.main' : 'success.main', sub: margin == null ? undefined : `${margin}% de margen` },
  ];
  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {tiles.map(x => (
          <Grid item xs={6} md={3} key={x.label}>
            <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
              <Typography variant="body2" color="text.secondary">{x.label} ({data.series.length} meses)</Typography>
              <Typography variant="h5" fontWeight={800} color={x.color}>{x.value}</Typography>
              {x.sub && <Typography variant="caption" color="text.secondary">{x.sub}</Typography>}
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle1" fontWeight={700}>Facturación vs. costos y gastos</Typography>
            <RevenueVsExpensesChart data={data} />
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle1" fontWeight={700}>Gastos por mes</Typography>
            <ExpensesByCategoryChart data={data} />
          </CardContent></Card>
        </Grid>
      </Grid>
      <Card variant="outlined"><CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Mes a mes</Typography>
        <MonthlyResultsTable data={data} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Beneficio = facturación − costo de la mercadería vendida − gastos (fijos + día a día). Los gastos fijos activos
          cuentan en todos los meses con su monto actual. Las compras de materia prima no se suman como gasto porque ya
          entran en el costo de la mercadería.
        </Typography>
      </CardContent></Card>
    </Box>
  );
};

export default ExpensesMonthlyView;
